import { useCallback, useEffect, useLayoutEffect, useState } from 'react'
import { createPortal } from 'react-dom'

const POPOVER_WIDTH = 320
const GAP = 12
const PADDING = 6

/**
 * Tutorial guiado: resalta, uno a uno, los elementos marcados con
 * `data-tour="<target>"` y muestra una explicación junto a cada uno.
 * Se navega con los botones o con ← / → y se cierra con Esc.
 *
 * Móntelo sólo mientras está abierto (`{open && <GuidedTour … />}`): así cada
 * apertura empieza en el primer paso.
 *
 * @component
 * @param {Object} props
 * @param {Array<{target: string, title: string, body: string}>} props.steps
 * @param {() => void} props.onClose
 * @returns {JSX.Element|null}
 */
const GuidedTour = ({ steps, onClose }) => {
  const [index, setIndex] = useState(0)
  const [rect, setRect] = useState(null)
  const step = steps[index]

  const measure = useCallback(() => {
    const el = step && document.querySelector(`[data-tour="${step.target}"]`)
    setRect(el ? el.getBoundingClientRect() : null)
  }, [step])

  // Lleva el elemento a la vista y mide su posición.
  useLayoutEffect(() => {
    if (!step) return
    const el = document.querySelector(`[data-tour="${step.target}"]`)
    el?.scrollIntoView({ block: 'center', behavior: 'smooth' })
    // Una medición inmediata y otra tras el scroll suave.
    const first = requestAnimationFrame(measure)
    const timer = setTimeout(measure, 350)
    return () => {
      cancelAnimationFrame(first)
      clearTimeout(timer)
    }
  }, [step, measure])

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowRight')
        setIndex((i) => Math.min(i + 1, steps.length - 1))
      if (e.key === 'ArrowLeft') setIndex((i) => Math.max(i - 1, 0))
    }
    window.addEventListener('keydown', onKey)
    window.addEventListener('resize', measure)
    window.addEventListener('scroll', measure, true)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('resize', measure)
      window.removeEventListener('scroll', measure, true)
    }
  }, [steps.length, onClose, measure])

  if (!step) return null

  const isLast = index === steps.length - 1
  const vw = window.innerWidth
  const vh = window.innerHeight
  const width = Math.min(POPOVER_WIDTH, vw - 32)

  // Debajo del elemento si cabe; si no, encima. Siempre dentro del viewport.
  let popoverStyle = { width, left: (vw - width) / 2, top: vh / 2 - 90 }
  if (rect) {
    const below = rect.bottom + GAP + 180 < vh
    popoverStyle = {
      width,
      left: Math.min(Math.max(16, rect.left), vw - width - 16),
      top: below ? rect.bottom + GAP : Math.max(16, rect.top - GAP - 180),
    }
  }

  return createPortal(
    <div
      className="gt-root"
      role="dialog"
      aria-modal="true"
      aria-label={step.title}
    >
      {/* Clic fuera del resaltado cierra el tutorial. */}
      <div className="gt-backdrop" onClick={onClose} />
      {rect && (
        <div
          className="gt-spotlight"
          style={{
            top: rect.top - PADDING,
            left: rect.left - PADDING,
            width: rect.width + PADDING * 2,
            height: rect.height + PADDING * 2,
          }}
        />
      )}

      <div className="gt-popover" style={popoverStyle}>
        <div className="d-flex justify-content-between align-items-center mb-1">
          <span className="gt-count">
            Paso {index + 1} de {steps.length}
          </span>
          <button
            type="button"
            className="gt-close"
            onClick={onClose}
            aria-label="Cerrar tutorial"
          >
            <i className="fa-solid fa-xmark" />
          </button>
        </div>
        <h6 className="gt-title">{step.title}</h6>
        <p className="gt-body">{step.body}</p>

        <div className="d-flex justify-content-between align-items-center">
          <div className="gt-dots">
            {steps.map((s, i) => (
              <span key={s.target} className={i === index ? 'is-active' : ''} />
            ))}
          </div>
          <div className="d-flex gap-2">
            {index > 0 && (
              <button
                type="button"
                className="btn btn-sm btn-light"
                onClick={() => setIndex(index - 1)}
              >
                Anterior
              </button>
            )}
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={() => (isLast ? onClose() : setIndex(index + 1))}
            >
              {isLast ? 'Terminar' : 'Siguiente'}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .gt-root { position: fixed; inset: 0; z-index: 2000; }
        .gt-backdrop { position: absolute; inset: 0; }
        .gt-spotlight {
          position: fixed; border-radius: 12px; pointer-events: none;
          box-shadow: 0 0 0 9999px rgba(15, 23, 42, 0.55), 0 0 0 2px var(--gm-primary);
          transition: all 0.25s ease;
        }
        .gt-popover {
          position: fixed; padding: 16px; border-radius: 14px;
          background: var(--gm-surface); color: var(--gm-text);
          border: 1px solid var(--gm-border); box-shadow: var(--gm-shadow-lg);
          transition: top 0.25s ease, left 0.25s ease;
        }
        .gt-count { font-size: 0.7rem; font-weight: 700; text-transform: uppercase; color: var(--gm-primary); }
        .gt-close { border: none; background: transparent; color: var(--gm-text-muted); }
        .gt-title { font-weight: 700; margin: 0 0 4px; }
        .gt-body { font-size: 0.85rem; color: var(--gm-text-muted); margin-bottom: 14px; }
        .gt-dots { display: flex; gap: 4px; }
        .gt-dots span { width: 6px; height: 6px; border-radius: 50%; background: var(--gm-border); }
        .gt-dots span.is-active { background: var(--gm-primary); width: 16px; border-radius: 99px; }
      `}</style>
    </div>,
    document.body
  )
}

export default GuidedTour
